import { create } from 'zustand'
import { api } from '@/lib/api'
import { captureEvent } from '@/lib/posthog'

// ===================
// Types
// ===================

export interface OnboardingStatusResponse {
  steps: Record<string, boolean>
  dismissedAt: string | null
  completedCount: number
  totalSteps: number
}

// ===================
// Store
// ===================

interface OnboardingState {
  steps: Record<string, boolean>
  dismissedAt: string | null
  completedCount: number
  totalSteps: number
  isLoading: boolean
  error: string | null

  fetchStatus: () => Promise<void>
  dismiss: () => Promise<void>
  /** Resolves true when the server accepted the restore, false when it failed. */
  restore: () => Promise<boolean>
}

export const useOnboardingStore = create<OnboardingState>()((set, get) => ({
  steps: {},
  dismissedAt: null,
  completedCount: 0,
  totalSteps: 12,
  isLoading: false,
  error: null,

  fetchStatus: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await api.get<OnboardingStatusResponse>('/users/onboarding')

      // Track newly completed steps
      const prevSteps = get().steps
      for (const [key, completed] of Object.entries(data.steps)) {
        if (completed && !prevSteps[key]) {
          captureEvent('onboarding_step_completed', { step: key })
        }
      }

      set({
        steps: data.steps,
        dismissedAt: data.dismissedAt,
        completedCount: data.completedCount,
        totalSteps: data.totalSteps,
        isLoading: false,
      })
    } catch {
      set({ isLoading: false, error: 'Failed to load onboarding status' })
    }
  },

  dismiss: async () => {
    // Optimistic update
    const prev = { dismissedAt: get().dismissedAt }
    set({ dismissedAt: new Date().toISOString() })

    captureEvent('onboarding_dismissed', {
      completedCount: get().completedCount,
      totalSteps: get().totalSteps,
    })
    try {
      await api.patch('/users/onboarding', { action: 'dismiss' })
    } catch {
      // Revert on failure
      set({ dismissedAt: prev.dismissedAt })
    }
  },

  restore: async () => {
    // Optimistic update
    const prev = { dismissedAt: get().dismissedAt }
    set({ dismissedAt: null })

    captureEvent('onboarding_restored')
    try {
      await api.patch('/users/onboarding', { action: 'restore' })
      return true
    } catch {
      // Revert on failure — the caller must not navigate as if it worked.
      set({ dismissedAt: prev.dismissedAt })
      return false
    }
  },
}))
